import * as THREE from 'three';
import type { Game } from './Game';
import { useStore } from '../state/store';

export type EntityKind = 'player' | 'enemy' | 'zeppelin' | 'bullet' | 'fx' | 'tank' | 'ally';

/** Boss part-HP multiplier read at spawn: bosses arriving later in the campaign are tougher,
 *  +50% by the final wave (24). Without this, boss HP was flat across the rotation and the
 *  back half of the campaign de-escalated. */
export function bossHpScale(): number {
  const wave = useStore.getState().wave;
  return 1 + 0.5 * Math.min(1, Math.max(0, (wave - 3) / 21));
}

/** Standing order for a controllable friendly unit, set from the tactical map. Unmanned
 *  units obey it; the player can still possess any unit and fly it directly. */
export type UnitOrder =
  | { type: 'patrol' }                          // roam and engage the nearest threat (default)
  | { type: 'defend'; villageIndex: number }    // hold station over a village, engage attackers
  | { type: 'escort'; unitId: number }          // stay with a friendly unit, engage its attackers
  | { type: 'attack'; targetId: number };       // hunt a specific enemy until it's gone

export interface EntityCtx {
  game: Game;
  scene: THREE.Scene;
  dt: number;
  now: number;
}

export abstract class Entity {
  id: number;
  kind: EntityKind;
  group = new THREE.Group();
  dead = false;
  radius = 1;
  hp = 1;
  ownerId?: number;
  /** Last time this entity took damage (game seconds). Used by the threat-alert system
   *  to know who's currently under attack and direct the player's attention. */
  lastDamageAt = -999;
  /** Local-space AABB used for OBB collision. If set, swept-bullet tests will use this
   *  (transformed by the entity's world matrix). If unset, the collision falls back to
   *  the sphere defined by `radius`. */
  localBounds?: THREE.Box3;
  /** Marks ground-based bosses (sub-entity kind 'tank') so the wave clear gate still
   *  waits on them instead of treating them like grunt tanks. */
  isBoss = false;
  /** If set, the bullet collision pass tests each part separately instead of `localBounds`.
   *  Each part has its own Object3D (so we can transform the bullet by that part's matrix
   *  rather than the entity root) and a local AABB. Hits dispatch to `damagePart()`. */
  hitParts?: { id: string; box: THREE.Box3; object: THREE.Object3D }[];
  /** Scripted opening-mission contact. Uncrewed allied AI ignores these so each lesson remains
   *  the player's action; the controlled unit can still target and destroy them normally. */
  trainingTarget = false;

  // --- Possession (commander "jump between units") ---
  /** Allied unit — fights for the player's side, jumpable, ignored by enemy fire unless controlled. */
  friendly = false;
  /** Can the player take direct control of this unit? */
  controllable = false;
  /** Currently the player-controlled unit? */
  controlled = false;
  /** Short label shown on the jump HUD (e.g. "FIGHTER", "AA GUN — Village 3", "TANK"). */
  unitLabel = '';
  /** Standing order set from the tactical map; obeyed by the unit's AI when not piloted. */
  order: UnitOrder = { type: 'patrol' };
  /** Enemy id this unit is currently aiming/firing at this frame (0 = none). Surfaced to
   *  the tactical map as a faint "currently engaging" line. Set by the unit each update. */
  engageTargetId = 0;
  /** Take control: attach the camera, switch from AI to player input. */
  possess(_camera: THREE.PerspectiveCamera): void { this.controlled = true; }
  /** Release control: detach the camera, revert to AI. */
  release(): void { this.controlled = false; }
  /** Engine-rev intensity [0..1] for audio. Planes override; ground units return 0. */
  getManeuverIntensity(): number { return 0; }
  /** Health fraction [0..1] for the jump HUD. Override where the unit tracks max HP. */
  hpFrac(): number { return 1; }

  private static _nextId = 1;

  constructor(kind: EntityKind) {
    this.kind = kind;
    this.id = Entity._nextId++;
  }

  get position(): THREE.Vector3 { return this.group.position; }

  abstract update(ctx: EntityCtx): void;

  /** Is this entity still a viable threat / target? Override on dying entities so the
   *  targeting systems (auto-fire, autopilot, reticles, radar) can skip / desaturate them. */
  alive(): boolean { return true; }

  damage(_amount: number, _ctx: EntityCtx): void {
    this.hp -= _amount;
    if (this.hp <= 0) this.die(_ctx);
  }

  /** Override to handle multi-part damage (used by SkyCarrier whose pods are hit
   *  individually). Default forwards to `damage()`. */
  damagePart(_partId: string, amount: number, ctx: EntityCtx): void {
    this.damage(amount, ctx);
  }

  die(_ctx: EntityCtx): void { this.dead = true; }

  dispose(): void {
    this.group.traverse((o) => {
      // Mesh, Line, Points all carry a `geometry` + `material` and should be released.
      const obj = o as THREE.Mesh | THREE.Line | THREE.Points;
      const isRenderable = (obj as THREE.Mesh).isMesh
        || (obj as THREE.Line).isLine
        || (obj as THREE.Points).isPoints;
      if (isRenderable) {
        const r = obj as THREE.Mesh; // narrow for property access — all three share these fields
        r.geometry?.dispose?.();
        const m = r.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
        else m?.dispose?.();
      }
    });
    this.group.removeFromParent();
  }
}
